/**
 * Copyright © 2025-2026 Indisea Software LLC. All rights reserved.
 *
 * Proprietary and confidential. Licensed, not sold. Use is subject to the
 * license agreement between Indisea Software LLC and the licensee.
 * See LICENSE for full terms.
 *
 * SPDX-License-Identifier: LicenseRef-Indisea-Proprietary
 */

import React, { useState, useEffect } from "react";
import {
    TextField,
    Box,
    Paper,
    Stack,
    Typography,
    Button,
    FormControl,
    Checkbox,
    ListItemText,
    FormControlLabel,
    Switch,
    Autocomplete,
} from "@mui/material";

import { useParams, useLocation } from "react-router-dom";
import apiFetch from "./apiFetch";
import apiHeaders from "./apiHeaders";
import { setRealmId } from "./storageUtils";

// Posting mode default for new customers - "Standard" (calculate + post), "PostOnly" (post without
// recalculating), or "NoPosting" (skip entirely). The actual options (canonical value + display
// label) are loaded from GET api/customer/posting-modes (see postingModeOptions state below) rather
// than hardcoded here, so a future posting mode never requires a frontend change. This is only the
// emergency fallback used if that fetch fails.
const DEFAULT_POSTING_MODE_OPTIONS = [{ value: "Standard", label: "Standard" }];

function ZampSettings() {
    const realmId = useParams();
    const location = useLocation();

    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [successMessage, setSuccessMessage] = useState("");

    // Retrieving realm id from url and saving to session storage
    useEffect(() => {
        if (realmId && realmId.realmId && realmId.realmId !== ":realmId") {
            setRealmId(realmId.realmId);
            window.dispatchEvent(new Event("realmIdChange"));
        }
    }, [location.search, realmId.realmId]);

    const handlePasswordChange = (event) => {
        setPassword(event.target.value);
        if (error) setError("");
    };

    // token authentication submit handle
    const handleTokenSubmit = async (event) => {
        event.preventDefault();
        const token = password;
        const data = {
            token,
            realmId: sessionStorage.getItem("realmId"),
        };

        if (password.length !== 64) {
            setSuccessMessage("");
            setError("API token must be exactly 64 characters.");
            return;
        }

        try {
            const result = await apiFetch("/api/auth/zamp", {
                method: "POST",
                body: JSON.stringify(data),
            });

            if (result === null) return; // Session expired, handled by apiFetch

            setSuccessMessage(result.message);
            setError("");
        } catch (err) {
            setError(err.message || "Internal server error. Please try again.");
            setSuccessMessage("");
        }
    };

    const [loadingOptions, setLoadingOptions] = useState(false);
    const [optionsError, setOptionsError] = useState("");

    // Toggle option states
    const [syncEnabled, setSyncEnabled] = useState(false);
    const [calculationsEnabled, setCalculationsEnabled] = useState(false);
    const [loggingEnabled, setLoggingEnabled] = useState(false);
    const [reCalculationsEnabled, setReCalculationsEnabled] = useState(false);
    // Default posting mode seeded onto new customers - "Standard", "PostOnly", or "NoPosting".
    const [defaultExternalSource, setDefaultExternalSource] = useState("Standard");
    const [postingModeOptions, setPostingModeOptions] = useState(DEFAULT_POSTING_MODE_OPTIONS);

    // Auto-disable recalculation if sync is turned off
    useEffect(() => {
        if (!syncEnabled) {
            setReCalculationsEnabled(false);
        }
    }, [syncEnabled]);

    // Populating stored user options
    useEffect(() => {
        const fetchZampToken = async () => {
            if (!sessionStorage.getItem('realmId')) return;
            
            try {
                const data = await apiFetch(`/api/zamp/token?realmId=${sessionStorage.getItem('realmId')}`);
                if (data === null) return; // Session expired
                
                if (data?.token) {
                    setPassword(data.token);
                }
            } catch (error) {
                setError(error.message);
            }
        };

        const fetchOptions = async () => {
            if (!sessionStorage.getItem('realmId')) return;
            
            setLoadingOptions(true);
            try {
                const data = await apiFetch(`/api/settings?realmId=${sessionStorage.getItem('realmId')}`);
                if (data === null) return; // Session expired
                
                setSyncEnabled(data["isSyncEnabled"]);
                setCalculationsEnabled(data["isCalculationsEnabled"]);
                setLoggingEnabled(data["isLoggingEnabled"]);
                setReCalculationsEnabled(data["isReCalculationsEnabled"]);
                setDefaultExternalSource(data["defaultExternalSource"] ?? "Standard");
            } catch (error) {
                // Silent fail for options
            } finally {
                setLoadingOptions(false);
            }
        };

        // Posting-mode options (value + display label) are loaded once here rather than hardcoded,
        // so a future posting mode added on the backend shows up without a frontend change.
        const fetchPostingModeOptions = async () => {
            try {
                const data = await apiFetch(`/api/customer/posting-modes`);
                if (data === null) return; // Session expired
                if (Array.isArray(data) && data.length) {
                    setPostingModeOptions(data);
                }
            } catch (error) {
                // Non-critical: keep the DEFAULT_POSTING_MODE_OPTIONS fallback.
            }
        };

        const fetchToken = async () => {
            const urlParams = new URLSearchParams(window.location.search);
            const sessionId = urlParams.get("s_id");

            if (sessionId) {
                try {
                    // Session exchange doesn't need auth token
                    const response = await fetch(
                        `${process.env.REACT_APP_CONNECTOR_API_URL}/api/session?sessionId=${sessionId}`,
                        {
                            method: "GET",
                            headers: apiHeaders
                        }
                    );
                    if (response.ok) {
                        const result = await response.json();
                        sessionStorage.setItem("token", result.token);
                    } else {
                        const result = await response.json();
                        setError(result.message);
                    }
                } catch (err) {
                    setError("Internal server error. Please try again.");
                }
                
                // Remove sessionId from URL without reloading
                urlParams.delete("s_id");
                const newUrl = `${window.location.pathname}${urlParams.toString() ? "?" + urlParams.toString() : ""}`;
                window.history.replaceState({}, "", newUrl);
            }
            
            fetchOptions();
            fetchZampToken();
            fetchPostingModeOptions();
        };

        fetchToken();
    }, []);

    const [saveSettingserror, setSaveSettingsError] = useState("");
    const [saveSettingsSuccessMessage, setSaveSettingsSuccessMessage] = useState("");

    // Handle custom user settings submit
    const handleSubmit = async (event) => {
        event.preventDefault();

        const data = {
            realmId: realmId.realmId,
            isSyncEnabled: syncEnabled,
            isCalculationsEnabled: calculationsEnabled,
            isLoggingEnabled: loggingEnabled,
            isReCalculationsEnabled: syncEnabled ? reCalculationsEnabled : false,
            DefaultExternalSource: defaultExternalSource
        };

        try {
            const result = await apiFetch("/api/settings", {
                method: "POST",
                body: JSON.stringify(data),
            });

            if (result === null) return; // Session expired, handled by apiFetch

            setSaveSettingsSuccessMessage("Settings saved successfully!");
            setSaveSettingsError("");
        } catch (err) {
            setSaveSettingsError(err.message || "Failed to save settings. Please try again.");
            setSaveSettingsSuccessMessage("");
        }
    };

    const [searchText, setSearchText] = useState("");

    return (
        <Box display="flex" flexDirection="column" gap={2} p={2}>
            <Box sx={{ alignItems: "center" }}>
                <Typography
                    variant="h6"
                    sx={{ mb: 2, fontWeight: "bold", textAlign: "center" }}
                >
                    Zamp Tax Settings
                </Typography>
            </Box>

            <Box component="form" onSubmit={handleTokenSubmit}>
                <Paper elevation={6} sx={{ p: 4, maxWidth: 800, margin: "auto" }}>
                    <Stack spacing={2} direction="column" sx={{ width: "100%" }}>
                        <TextField
                            label="Zamp API Token"
                            type="password"
                            variant="outlined"
                            fullWidth
                            required
                            value={password}
                            onChange={handlePasswordChange}
                            name="password"
                        />
                        {error && <Typography color="error">{error}</Typography>}
                        {successMessage && (
                            <Typography color="success">{successMessage}</Typography>
                        )}
                        <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            className="primary"
                            sx={{ width: "250px" }}
                        >
                            Update API Token
                        </Button>
                    </Stack>
                </Paper>
            </Box>

            <Box component="form" onSubmit={handleSubmit}>
                <Paper elevation={6} sx={{ p: 4, maxWidth: 800, margin: "auto" }}>
                    {loadingOptions ? (
                        <Typography>Loading options...</Typography>
                    ) : optionsError ? (
                        <Typography color="error">{optionsError}</Typography>
                    ) : (null
                    )}

                    <Stack spacing={2} direction="column" sx={{ width: "100%", mt: 2 }}>
                        <FormControlLabel
                            alignItems="flex-start"
                            sx={{ ml: 0 }}
                            control={
                                <Switch
                                    checked={calculationsEnabled}
                                    onChange={() => setCalculationsEnabled(!calculationsEnabled)}
                                    sx={{ mt: 0.5 }}
                                />
                            }
                            label={
                                <Box>
                                    <Typography variant="subtitle1">
                                        Allow Zamp to handle tax calculations
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Enable Zamp to automatically calculate tax on transactions.
                                    </Typography>
                                </Box>
                            }
                        />
                        <FormControlLabel
                            alignItems="flex-start"
                            sx={{ ml: 0 }}
                            control={
                                <Switch
                                    checked={syncEnabled}
                                    onChange={() => setSyncEnabled(!syncEnabled)}
                                    sx={{ mt: 0.5 }}
                                />
                            }
                            label={
                                <Box>
                                    <Typography variant="subtitle1">
                                        Sync transactions with Zamp
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Required for historical transaction sync functionality.
                                    </Typography>
                                </Box>
                            }
                        />
                        <FormControlLabel
                            alignItems="flex-start"
                            sx={{ ml: 0 }}
                            control={
                                <Switch
                                    checked={loggingEnabled}
                                    onChange={() => setLoggingEnabled(!loggingEnabled)}
                                    sx={{ mt: 0.5 }}
                                />
                            }
                            label={
                                <Box>
                                    <Typography variant="subtitle1">
                                        Retain Zamp logs for 30 days
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Enable debug logging for troubleshooting. Logs are retained for 30 days.
                                    </Typography>
                                </Box>
                            }
                        />
                        <FormControlLabel
                            alignItems="flex-start"
                            sx={{ ml: 0 }}
                            control={
                                <Switch
                                    checked={reCalculationsEnabled}
                                    onChange={() => setReCalculationsEnabled(!reCalculationsEnabled)}
                                    disabled={!syncEnabled}
                                    sx={{ mt: 0.5 }}
                                />
                            }
                            label={
                                <Box>
                                    <Typography variant="subtitle1">
                                        Enable transaction sync recalculation
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Recalculate transactions for Zamp. Requires "Sync transactions with Zamp" to be enabled.
                                    </Typography>
                                </Box>
                            }
                        />

                        <Box sx={{ mt: 1 }}>
                            <Typography variant="subtitle1">
                                Default Posting Mode for New Customers
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                Standard calculates and posts; Post Only posts without recalculating; No Posting skips the customer entirely.
                            </Typography>
                            <Autocomplete
                                options={postingModeOptions.map((o) => o.label)}
                                value={
                                    postingModeOptions.find((o) => o.value === defaultExternalSource)?.label
                                    ?? postingModeOptions[0]?.label
                                }
                                onChange={(e, newValue) =>
                                    setDefaultExternalSource(
                                        postingModeOptions.find((o) => o.label === newValue)?.value ?? postingModeOptions[0]?.value
                                    )
                                }
                                isOptionEqualToValue={(option, value) => option === value}
                                disableClearable
                                sx={{ maxWidth: 300 }}
                                renderInput={(params) => (
                                    <TextField {...params} size="small" placeholder="Select" />
                                )}
                            />
                        </Box>

                        {saveSettingserror && <Typography color="error">{saveSettingserror}</Typography>}
                        {saveSettingsSuccessMessage && (
                            <Typography color="success">{saveSettingsSuccessMessage}</Typography>
                        )}
                        <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            sx={{ width: "250px" }}
                        >
                            Save Settings
                        </Button>
                    </Stack>
                </Paper>
            </Box>
        </Box>
    );
}

export default ZampSettings;
